import type { DealFieldDef } from '@/services/dealFile'

/**
 * Plain-English field search: 'loan to value' names `ltv`, 'cap rate' names
 * `cap_rate`.
 *
 * ⌘K and the DB console resolve a query to catalogue fields on the server
 * (backend/app/underwriting/field_search.py, `match_fields`). The Audit tab's
 * All-fields box does the same here, over the catalogue the dossier already
 * holds: a field is named by its key or its label read as words, or by one of
 * the everyday names the server serves for it (`plain_english` on
 * GET /loans/field-definitions). The browser keeps no list of names of its own.
 *
 * Both are held to one fixture, backend/tests/fixtures/field_search_parity.json,
 * so a change to how either reads a query fails the other side's tests.
 */

/** The catalogue as the server serves it, of which a query is read. */
export type FieldSearchCatalogue = {
  fields: readonly Pick<DealFieldDef, 'name' | 'label'>[]
  plain_english?: Readonly<Record<string, readonly string[]>>
}

/** Lower case, with every run of anything but letters, digits and `&` (spaces,
 *  underscores, hyphens, punctuation) reduced to one space. */
export function normalize(text: string): string {
  return (text.toLowerCase().match(/[a-z0-9&]+/g) ?? []).join(' ')
}

/**
 * The catalogue fields a query names: it is one of a field's names, or a
 * whole-word run inside one — 'ltv' names 'LTV (Stabilized)', while 'cap' does
 * not name 'Capital Program'. The same words in another order name nothing, and
 * a query under two characters names nothing: the cut-off ⌘K and the DB console
 * list from.
 */
export function namedFieldKeys(query: string, catalogue: FieldSearchCatalogue | undefined): Set<string> {
  const named = new Set<string>()
  const words = normalize(query)
  if (!catalogue || words.length < 2) return named
  const run = ` ${words} `
  for (const { name, label } of catalogue.fields) {
    const names = [name, label, ...(catalogue.plain_english?.[name] ?? [])]
    if (names.some(term => ` ${normalize(term)} `.includes(run))) named.add(name)
  }
  return named
}
